import { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Plus, CreditCard, Check, Trash2, Loader2, Coins } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { bonndleAuthApi } from '../../services/bonndleAuthApi';
import { useResponsive } from '../../hooks/useResponsive';
import { convertToLocalCurrency, getCountryCode } from '../../utils/currency';

export default function PaymentWallet({ onBack }) {
  const {
    payWithRewardsEnabled,
    setPayWithRewardsEnabled,
    setShowCardForm,
  } = useAppState();
  const { user, rewardsData, fetchUserData } = useAuth();
  const { isDesktop } = useResponsive();

  // Cards state - fetched from API
  const [fetchedCards, setFetchedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [balanceLoading, setBalanceLoading] = useState(true);

  // Get user's country code for currency conversion
  const userCountryCode = useMemo(() => {
    const countryValue = user?.country || user?.country_code || '';
    return getCountryCode(countryValue) || 'US';
  }, [user]);

  // Calculate rewards balance (use same fallbacks as other components)
  const rewardsBalance = useMemo(() => {
    const fromUser = user?.available_bold_credits || user?.bold_credits || user?.boldCredits;
    const fromRewardsData = rewardsData?.balance;
    return fromUser || fromRewardsData || 0;
  }, [user?.available_bold_credits, user?.bold_credits, user?.boldCredits, rewardsData?.balance]);

  // Convert points to USD (1000 points = $1 USD)
  const rewardsInUSD = useMemo(() => {
    return rewardsBalance / 1000;
  }, [rewardsBalance]);

  // Convert to local currency (from USD, not points)
  const rewardsInLocalCurrency = useMemo(() => {
    if (rewardsInUSD <= 0) return null;
    return convertToLocalCurrency(rewardsInUSD, userCountryCode);
  }, [rewardsInUSD, userCountryCode]);

  // Fetch user data and cards on mount
  useEffect(() => {
    const fetchData = async () => {
      setCardsLoading(true);
      setBalanceLoading(true);

      try {
        // Fetch user data and cards in parallel
        const [, cardsResult] = await Promise.allSettled([
          fetchUserData(),
          bonndleAuthApi.getCards(),
        ]);

        // Update cards if successful
        if (cardsResult.status === 'fulfilled') {
          const cards = Array.isArray(cardsResult.value) ? cardsResult.value : [];
          setFetchedCards(cards);
        }
      } catch (err) {
        console.error('Error fetching wallet data:', err);
      } finally {
        setCardsLoading(false);
        setBalanceLoading(false);
      }
    };

    fetchData();
  }, [fetchUserData]);

  const handleSetDefault = async (cardId) => {
    // TODO: Call API to set default card
    // For now, update local state
    setFetchedCards((prev) =>
      prev.map((card) => ({
        ...card,
        isDefaultCard: (card.uuid || card.id) === cardId,
      }))
    );
  };

  const handleDelete = async (cardId) => {
    // TODO: Call API to delete card
    // For now, update local state
    setFetchedCards((prev) => prev.filter((card) => (card.uuid || card.id) !== cardId));
  };

  return (
    <div className="flex-1 overflow-y-auto scrollbar-hide bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] flex items-center gap-3 border-b border-gray-100">
        <button onClick={onBack} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-lg font-bold text-gray-900">Payment & Wallet</h1>
      </header>

      <div className={`px-4 py-6 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
        {/* Balance Card */}
        <div className="bg-gradient-card rounded-2xl p-6 text-white mb-6 relative overflow-hidden">
          {/* Background coin icons */}
          <Coins size={80} className="absolute -right-4 -top-4 text-white/10 rotate-12" />
          <Coins size={60} className="absolute right-16 bottom-2 text-white/10 -rotate-12" />
          <Coins size={40} className="absolute left-1/2 -top-2 text-white/10 rotate-45" />

          <p className="text-sm text-white/70 mb-1 relative z-10">Bonndle Rewards Balance</p>
          {balanceLoading ? (
            <div className="flex items-center gap-2 relative z-10">
              <Loader2 size={24} className="animate-spin" />
              <span className="text-lg">Loading...</span>
            </div>
          ) : (
            <div className="relative z-10">
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold">{Math.floor(rewardsBalance).toLocaleString()}</span>
                <sup className="text-sm text-white/70 font-medium">PTS</sup>
              </div>
              <p className="text-base text-white/90">USD {rewardsInUSD.toFixed(2)}</p>
              {rewardsInLocalCurrency && userCountryCode !== 'US' && (
                <p className="text-sm text-white/70 mt-1">
                  ≈ {rewardsInLocalCurrency.formatted}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Pay with Rewards Toggle */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-900">Pay with Rewards + Card</p>
              <p className="text-sm text-gray-500">Auto-apply rewards to purchases</p>
            </div>
            <button
              onClick={() => setPayWithRewardsEnabled(!payWithRewardsEnabled)}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                payWithRewardsEnabled ? 'bg-indigo-600' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  payWithRewardsEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Saved Cards */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-bold text-gray-900">Saved Cards</h2>
          </div>

          {cardsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-indigo-600" />
              <span className="ml-2 text-gray-500">Loading cards...</span>
            </div>
          ) : fetchedCards.length === 0 ? (
            <div className="p-6 text-center">
              <CreditCard size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No saved cards</p>
              <p className="text-sm text-gray-400">Add a card to make payments easier</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {fetchedCards.map((card) => {
                // API returns: cardProvider (VISA), last4Digits, expMonth, expYear, uuid, isDefaultCard
                const cardBrand = card.cardProvider || card.brand || 'Card';
                const cardLast4 = card.last4Digits || card.last4 || '****';
                const cardExpiry = card.expMonth && card.expYear ? `${card.expMonth}/${card.expYear}` : '';
                const cardId = card.uuid || card.id;
                const isDefault = card.isDefaultCard;

                return (
                  <div key={cardId} className="p-4">
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-8 rounded flex items-center justify-center text-white text-xs font-bold ${
                          cardBrand.toUpperCase() === 'VISA' ? 'bg-blue-600' :
                          cardBrand.toUpperCase() === 'MASTERCARD' ? 'bg-orange-500' :
                          'bg-gray-600'
                        }`}
                      >
                        {cardBrand.toUpperCase() === 'VISA' ? 'VISA' :
                         cardBrand.toUpperCase() === 'MASTERCARD' ? 'MC' :
                         cardBrand.substring(0, 4).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">•••• {cardLast4}</p>
                        {cardExpiry && <p className="text-sm text-gray-500">Expires {cardExpiry}</p>}
                      </div>
                      {isDefault && (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 mt-3">
                      {!isDefault && (
                        <button
                          onClick={() => handleSetDefault(cardId)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        >
                          <Check size={14} />
                          Set as Default
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(cardId)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={14} />
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add Card Button */}
        <button
          onClick={() => setShowCardForm(true)}
          className="w-full flex items-center justify-center gap-2 py-4 bg-white rounded-2xl shadow-sm text-indigo-600 font-semibold hover:bg-indigo-50 transition-colors"
        >
          <Plus size={20} />
          Add New Card
        </button>
      </div>
    </div>
  );
}
